import * as React from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  KitchenArrivalRow,
  PbvRally,
  PbvShot,
  ShotNumberOverlay,
} from "./RallyBoardPopup";

import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { VideoOverlayControls } from "./GameStory_FastShotsPopup";
import { ShotNumberOverlayLayer } from "./RallyPopup";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { FirefightClip } from "./GameStory_firefights";
import {
  iconReaction,
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import { ActiveRallyVideo } from "./ActiveRallyVideo";

// fastReactions.ts
export type FastReactionEvent = {
  rallyIndex: number;
  shotIndex: number;
  shotNumber: number;

  team: 0 | 1;
  playerId: number;

  reactionMs: number;
  thresholdMs: number;

  // shot timing for seeking / display
  shotStartMs: number;
  shotEndMs: number;

  // optional UI bits
  isVolley?: boolean;
  strokeType?: string;
  shotType?: string;
};

// function shotWentOut(shot: any): boolean {
//   // Don’t count if they go out.
//   // Your data has resulting_ball_movement.trajectory.end.zone and we’ve seen "out".
//   return shot?.resulting_ball_movement?.trajectory?.end?.zone === "out";
// }

// function shotWentOut(overlay: ShotNumberOverlay): boolean {
//   return (
//     overlay?.rawShot?.resulting_ball_movement?.trajectory?.end?.zone === "out"
//   );
// }

function shotIsBad(overlay: ShotNumberOverlay): boolean {
  const s = overlay?.rawShot;

  // OUT via trajectory end zone
  if (s?.resulting_ball_movement?.trajectory?.end?.zone === "out") return true;

  const faults = s?.errors?.faults;

  // If PBV flags net faults explicitly
  if (faults?.net === true) return true;
  //if (faults?.tape === true) return true;

  // Some datasets store as strings/arrays
  const faultStr = JSON.stringify(faults ?? {});
  if (faultStr.includes("net") || faultStr.includes("tape")) return true;

  return false;
}

export function computeFastReactionsForRally(args: {
  rally: KitchenArrivalRow;
  rallyIndex: number;
  thresholdMs: number;
  getTeamFromPlayerId: (playerId: number) => 0 | 1;
}): FastReactionEvent[] {
  const { rally, rallyIndex, thresholdMs, getTeamFromPlayerId } = args;

  // console.error("rally", { rally });

  //   const shots: ShotNumberOverlay[] = Array.isArray(rally?.shotNumberOverlays)
  //     ? rally.shotNumberOverlays
  //     : [];

  const shots = [
    ...(Array.isArray(rally?.shotNumberOverlays)
      ? rally.shotNumberOverlays
      : []),
  ].sort((a, b) => (a.rawShot?.start_ms ?? 0) - (b.rawShot?.start_ms ?? 0));

  const out: FastReactionEvent[] = [];
  // console.error("shots", { shots });

  for (let i = 1; i < shots.length; i++) {
    const prev = shots[i - 1];
    const cur = shots[i];

    const prevStart = prev?.rawShot?.start_ms ?? undefined;
    const prevEnd = prev?.rawShot?.end_ms ?? prev?.rawShot?.start_ms;
    const curStart = cur?.rawShot?.start_ms;
    const curEnd = cur?.rawShot?.end_ms ?? cur?.rawShot?.start_ms;

    if (
      typeof prevStart !== "number" ||
      typeof prevEnd !== "number" ||
      typeof curStart !== "number" ||
      typeof curEnd !== "number"
    )
      continue;

    //const reactionMs = curStart - prevEnd;

    const reactionMs = curStart - prevStart; //prev?.rawShot?.start_ms; //curContactMs - prevContactMs

    if (reactionMs < 0) continue;
    if (reactionMs > thresholdMs) continue;
    if (shotIsBad(cur)) continue;

    const playerId = Number(cur.playerId);

    out.push({
      rallyIndex,
      shotIndex: i,
      shotNumber: i + 1,
      team: getTeamFromPlayerId(playerId),
      playerId,
      reactionMs,
      thresholdMs,
      shotStartMs: curStart,
      shotEndMs: curEnd,
      isVolley: !!cur.rawShot?.is_volley,
      strokeType: cur.rawShot?.stroke_type,
      shotType: cur.rawShot?.shot_type,
    });
  }

  return out;
}

export type FastReactionClip = {
  key: string;
  event: FastReactionEvent;

  // clip window you request from /api/rally-segment-video
  clipStartMs: number;
  clipEndMs: number;
};

export function buildFastReactionClip(args: {
  event: FastReactionEvent;
  // these are global knobs you can tweak later
  preMs?: number;
  postMs?: number;
  // optional clamp if you know rally bounds
  rallyStartMs?: number;
  rallyEndMs?: number;
}): FastReactionClip {
  const { event } = args;
  const preMs = args.preMs ?? 900;
  const postMs = args.postMs ?? 700;

  let clipStartMs = event.shotStartMs - preMs;
  let clipEndMs = event.shotEndMs + postMs;

  if (typeof args.rallyStartMs === "number")
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  if (typeof args.rallyEndMs === "number")
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);

  // hard minimum size just to avoid tiny segments if something is weird
  if (clipEndMs - clipStartMs < 400) clipEndMs = clipStartMs + 400;

  return {
    key: `r${event.rallyIndex}_s${event.shotIndex}_t${event.thresholdMs}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    event,
    clipStartMs,
    clipEndMs,
  };
}

function finiteMs(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeMaybeAbsoluteMs(
  value: unknown,
  clipStartMs: number,
  clipEndMs: number,
): number | undefined {
  const ms = finiteMs(value);
  if (ms == null) return undefined;

  // Most PBV overlay times are full-match/rally absolute milliseconds. Some
  // generated rows are already clip-relative. Keep both forms working.
  if (ms >= clipStartMs - 3000 && ms <= clipEndMs + 3000) {
    return Math.max(0, ms - clipStartMs);
  }

  const clipDurationMs = Math.max(0, clipEndMs - clipStartMs);
  if (ms >= -3000 && ms <= clipDurationMs + 3000) {
    return Math.max(0, ms);
  }

  return Math.max(0, ms);
}

function getOverlayAbsoluteStartMs(overlay: ShotNumberOverlay): number | null {
  return (
    finiteMs((overlay as any)?.rawShot?.start_ms) ??
    finiteMs((overlay as any)?.atMsRel) ??
    finiteMs((overlay as any)?.rawShot?.start_ms_rel) ??
    finiteMs((overlay as any)?.trajectoryStart?.ms) ??
    finiteMs(
      (overlay as any)?.rawShot?.resulting_ball_movement?.trajectory?.start?.ms,
    )
  );
}

function normalizeFastReactionClipOverlays(args: {
  overlays: ShotNumberOverlay[] | undefined;
  clipStartMs: number;
  clipEndMs: number;
  selectedShotNumber: number;
}): ShotNumberOverlay[] {
  const { overlays, clipStartMs, clipEndMs, selectedShotNumber } = args;
  if (!overlays?.length) return [];

  const clipDurationMs = Math.max(0, clipEndMs - clipStartMs);
  const keepStartMs = clipStartMs - 250;
  const keepEndMs = clipEndMs + 250;

  const normalized = overlays
    .filter((overlay) => {
      if (overlay.shotNumber === selectedShotNumber) return true;
      const absStartMs = getOverlayAbsoluteStartMs(overlay);
      if (absStartMs == null) return false;
      return absStartMs >= keepStartMs && absStartMs <= keepEndMs;
    })
    .map((overlay) => {
      const absStartMs = getOverlayAbsoluteStartMs(overlay);
      const atMsRel =
        absStartMs == null
          ? finiteMs((overlay as any).atMsRel) ?? 0
          : Math.max(0, Math.min(clipDurationMs, absStartMs - clipStartMs));

      const showFromMsRel =
        normalizeMaybeAbsoluteMs(
          (overlay as any).showFromMsRel,
          clipStartMs,
          clipEndMs,
        ) ?? Math.max(0, atMsRel - 200);
      const hideAtMsRel =
        normalizeMaybeAbsoluteMs(
          (overlay as any).hideAtMsRel,
          clipStartMs,
          clipEndMs,
        ) ?? Math.min(clipDurationMs, atMsRel + 700);

      return {
        ...overlay,
        atMsRel,
        showFromMsRel,
        fullFromMsRel:
          normalizeMaybeAbsoluteMs(
            (overlay as any).fullFromMsRel,
            clipStartMs,
            clipEndMs,
          ) ?? showFromMsRel,
        fadeStartMsRel:
          normalizeMaybeAbsoluteMs(
            (overlay as any).fadeStartMsRel,
            clipStartMs,
            clipEndMs,
          ) ?? Math.max(showFromMsRel, hideAtMsRel - 250),
        hideAtMsRel,
      };
    });

  return normalized.sort((a, b) => (a.atMsRel ?? 0) - (b.atMsRel ?? 0));
}

export function buildRallySegmentUrl(args: {
  packageId: string;
  rallyIndex: number;
  startMs: number;
  endMs: number;
  download?: 0 | 1;
}): string {
  const { packageId, rallyIndex, startMs, endMs } = args;
  return (
    `/api/rally-segment-video` +
    `?packageId=${encodeURIComponent(packageId)}` +
    `&rallyIndex=${encodeURIComponent(String(rallyIndex))}` +
    `&startMs=${encodeURIComponent(String(startMs))}` +
    `&endMs=${encodeURIComponent(String(endMs))}` +
    `&download=${encodeURIComponent(String(args.download ?? 1))}`
  );
}

function fastReactionCardWidth(previewScale: 1 | 2 | 4 | 8): number {
  return previewScale === 1
    ? 820
    : previewScale === 2
      ? 580
      : previewScale === 4
        ? 410
        : 300;
}

function FastReactionScaleControls(props: {
  value: 1 | 2 | 4 | 8;
  onChange: (value: 1 | 2 | 4 | 8) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.35,
        p: 0.25,
        border: "1px solid rgba(25,118,210,0.20)",
        borderRadius: 1.25,
        bgcolor: "rgba(25,118,210,0.045)",
      }}
    >
      {([
        [1, "Full"],
        [2, "1/2"],
        [4, "1/4"],
        [8, "1/8"],
      ] as const).map(([value, label]) => (
        <Button
          key={value}
          size="small"
          variant={props.value === value ? "contained" : "text"}
          onClick={() => props.onChange(value)}
          sx={{
            minWidth: 42,
            height: 24,
            px: 0.75,
            py: 0,
            textTransform: "none",
            fontSize: 11,
            fontWeight: 950,
          }}
        >
          {label}
        </Button>
      ))}
    </Box>
  );
}

function FastReactionPreviewCard(props: {
  clip: FastReactionClip;
  index: number;
  cardWidth: number;
  packageId: string;
  rows: KitchenArrivalRow[];
  insightsToCvPlayerIndex: PlayerIndexMap;
  renderPlayerIdentity: RenderPlayerIdentity;
  teamColor: (team: 0 | 1, alpha?: number) => string;
  teamName?: (team: 0 | 1) => string;
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const [hasEnteredView, setHasEnteredView] = React.useState(false);

  React.useEffect(() => {
    if (hasEnteredView) return;
    const node = cardRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setHasEnteredView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasEnteredView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasEnteredView]);

  const row =
    props.rows.find((r) => r.rallyIndex === props.clip.event.rallyIndex) ??
    props.rows[props.clip.event.rallyIndex] ??
    null;

  const clipUrl = React.useMemo(
    () =>
      buildRallySegmentUrl({
        packageId: props.packageId,
        rallyIndex: props.clip.event.rallyIndex,
        startMs: Math.round(props.clip.clipStartMs),
        endMs: Math.round(props.clip.clipEndMs),
        download: 0,
      }),
    [
      props.packageId,
      props.clip.event.rallyIndex,
      props.clip.clipStartMs,
      props.clip.clipEndMs,
    ],
  );

  const clipRow = React.useMemo(() => {
    if (!row) return null;
    return {
      ...row,
      videoUrl: clipUrl,
      segmentStartMs: props.clip.clipStartMs,
      segmentEndMs: props.clip.clipEndMs,
      shotNumberOverlays: normalizeFastReactionClipOverlays({
        overlays: row.shotNumberOverlays,
        clipStartMs: props.clip.clipStartMs,
        clipEndMs: props.clip.clipEndMs,
        selectedShotNumber: props.clip.event.shotNumber,
      }),
    };
  }, [row, clipUrl, props.clip.key]);

  const clipDurationSec = Math.max(
    0.4,
    (props.clip.clipEndMs - props.clip.clipStartMs) / 1000,
  );
  const selectedShotClipTimeSec = Math.max(
    0,
    (props.clip.event.shotStartMs - props.clip.clipStartMs) / 1000,
  );
  const loopB = Math.min(
    clipDurationSec,
    Math.max(
      selectedShotClipTimeSec + 0.45,
      (props.clip.event.shotEndMs - props.clip.clipStartMs) / 1000 + 0.25,
    ),
  );
  const reactionTitleNode = (
    <Box
      sx={{
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
      }}
    >
      <Typography
        sx={{
          color: "inherit",
          fontSize: 14,
          fontWeight: 950,
          whiteSpace: "nowrap",
        }}
      >
        player {props.clip.event.playerId}
      </Typography>
      {props.renderPlayerIdentity({
        playerId: props.clip.event.playerId,
        labelOnly: false,
        size: 28,
        borderRadius: 5,
        fontSize: 12,
      })}
      <Typography
        sx={{
          minWidth: 0,
          color: "inherit",
          fontSize: 14,
          fontWeight: 950,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {Math.round(props.clip.event.reactionMs)}ms reaction on shot{" "}
        {props.clip.event.shotNumber}
      </Typography>
    </Box>
  );

  return (
    <Box
      ref={cardRef}
      sx={{
        width: props.cardWidth,
        maxWidth: "calc(100vw - 112px)",
        minWidth: 0,
        flex: "0 0 auto",
        height: "100%",
        border:
          props.index === 0
            ? "2px solid rgba(128,216,255,0.84)"
            : "1px solid rgba(128,216,255,0.28)",
        borderRadius: 1.5,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        bgcolor: "rgba(0,0,0,0.46)",
        boxShadow:
          props.index === 0
            ? "0 0 0 3px rgba(41,182,246,0.20), inset 0 3px 0 rgba(25,118,210,0.9)"
            : "inset 0 3px 0 rgba(25,118,210,0.56)",
      }}
    >
      <Box
        sx={{
          flex: "0 0 auto",
          px: 0.9,
          py: 0.65,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.65 }}>
          <Typography
            sx={{
              color: "white",
              fontSize: 12,
              fontWeight: 950,
              whiteSpace: "nowrap",
            }}
          >
            R{props.clip.event.rallyIndex + 1} S{props.clip.event.shotNumber}
          </Typography>
          {props.renderPlayerIdentity({
            playerId: props.clip.event.playerId,
            labelOnly: false,
            size: 30,
            borderRadius: 5,
            fontSize: 12,
          })}
        </Box>
        <Typography
          sx={{
            color: "white",
            fontSize: 12,
            fontWeight: 950,
            whiteSpace: "nowrap",
          }}
        >
          {Math.round(props.clip.event.reactionMs)}ms reaction
        </Typography>
        <Typography
          sx={{
            color: props.teamColor(props.clip.event.team, 1),
            fontSize: 11,
            fontWeight: 950,
            whiteSpace: "nowrap",
          }}
        >
          {props.teamName
            ? props.teamName(props.clip.event.team)
            : `Team ${props.clip.event.team}`}
        </Typography>
      </Box>
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          position: "relative",
          bgcolor: "#000",
          "& .active-rally-video-root": {
            height: "100%",
            minHeight: 0,
          },
          "& video": {
            width: "100% !important",
            height: "100% !important",
            objectFit: "cover !important",
            objectPosition: "center 45%",
          },
        }}
      >
        {clipRow && hasEnteredView ? (
          <ActiveRallyVideo
            insightsToCvPlayerIndex={props.insightsToCvPlayerIndex}
            renderPlayerIdentity={props.renderPlayerIdentity}
            aiContextLabel="Fast Reactions"
            focusedFilter={null}
            packageId={props.packageId}
            row={clipRow}
            rows={props.rows}
            titleNode={reactionTitleNode}
            activeShotNumber={props.clip.event.shotNumber}
            showCondensedTimeline={false}
            fontColor="white"
            controlsPlacement="left"
            firefightsClips={props.firefightsClips}
            firefightRallySet={props.firefightRallySet}
            showRateControls={false}
            showTitle={false}
            showScoreEtc={false}
            showAi={false}
            aiDrawerMode="split"
            showLoopScrubber={false}
            defaultLoopUiOpen={false}
            showPopoutButton
            showPreviewPlayPauseButton
            isInFullPopup={false}
            seedPlayback={{
              timeSec: selectedShotClipTimeSec,
              paused: true,
              nonce: props.index + 1,
            }}
            seedLoopRegion={{
              enabled: true,
              aSec: selectedShotClipTimeSec,
              bSec: loopB,
              nonce: props.index + 1,
            }}
            seekToActiveShotOnChange={false}
            showShotLabels={false}
            showTimelineTicks={false}
            previewMode
            forceMuted
          />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "rgba(255,255,255,0.58)",
              fontSize: 13,
              fontWeight: 850,
            }}
          >
            {clipRow ? "Scroll into view to load video." : "Missing rally video."}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// FastReactionsPopup.tsx

type Mode = "thisRally" | "allRallies";

export function FastReactionsPopup(props: {
  insightsToCvPlayerIndex: PlayerIndexMap;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;

  packageId: string;

  // current selection context
  activeRallyIndex: number | null;
  rows: KitchenArrivalRow[];

  // derived data that you compute outside (recommended)
  clipsThisRally: FastReactionClip[];
  clipsAll: FastReactionClip[];

  // optional helpers for display
  playerLabel?: (playerId: number) => string;
  renderPlayerIdentity: RenderPlayerIdentity;
  teamLabel?: (team: 0 | 1) => string;

  teamColor: (team: 0 | 1, alpha?: number) => string;
  teamName?: (team: 0 | 1) => string; // e.g. "Us" / "Them" or "Team 0" / "Team 1"
  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const {
    open,
    anchorEl,
    onClose,
    packageId,
    activeRallyIndex,
    clipsThisRally,
    clipsAll,
    playerLabel,
    teamLabel,
    rows,
    teamColor,
    teamName,
    firefightsClips,
    firefightRallySet,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
  } = props;

  //   const [mode, setMode] = React.useState<Mode>("thisRally");
  const clips = clipsAll; //mode === "thisRally" ? clipsThisRally : clipsAll;

  // keep a selected clip
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    // When opening or switching modes, default select first clip
    if (!open) return;
    const first = clips[0]?.key ?? null;
    setSelectedKey(first);
  }, [open, activeRallyIndex]); // activeRallyIndex changes should reset selection

  const selected = clips.find((c) => c.key === selectedKey) ?? clips[0] ?? null;

  // Build URL for the selected clip
  const videoUrl = React.useMemo(() => {
    if (!selected) return null;
    return buildRallySegmentUrl({
      packageId,
      rallyIndex: selected.event.rallyIndex,
      startMs: Math.round(selected.clipStartMs),
      endMs: Math.round(selected.clipEndMs),
      download: 0, //1,
    });
  }, [packageId, selected?.key]);

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [playbackRate, setPlaybackRate] = React.useState<0.25 | 0.5 | 1>(1);

  const applyRate = React.useCallback((rate: 0.25 | 0.5 | 1) => {
    const v = videoRef.current;
    if (!v) return;

    // Chrome often respects defaultPlaybackRate more reliably across reload/loop.
    v.defaultPlaybackRate = rate;
    v.playbackRate = rate;

    console.log("applied rate", rate, "actual", v.playbackRate);
  }, []);

  React.useLayoutEffect(() => {
    if (!videoUrl) return;
    applyRate(playbackRate);
  }, [videoUrl, playbackRate, applyRate]);

  //   React.useEffect(() => {
  //     // do it now...
  //     applyRateAndPlay();

  //     // ...and again on next frames (ref/video sometimes not ready yet)
  //     const id1 = requestAnimationFrame(() => applyRateAndPlay());
  //     // const id2 = requestAnimationFrame(() => applyRateAndPlay());

  //     return () => {
  //       cancelAnimationFrame(id1);
  //       //   cancelAnimationFrame(id2);
  //     };
  //   }, [videoUrl, applyRateAndPlay]);

  const [pos, setPos] = React.useState<{ x: number; y: number }>({
    x: 18,
    y: 50,
  });

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      const nextX = Math.max(
        0,
        Math.min(window.innerWidth - 220, d.startPosX + dx),
      );
      const nextY = Math.max(
        0,
        Math.min(window.innerHeight - 140, d.startPosY + dy),
      );
      setPos({ x: nextX, y: nextY });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  const desiredRateRef = React.useRef<0.25 | 0.5 | 1>(1);

  React.useEffect(() => {
    desiredRateRef.current = playbackRate;
    // Apply instantly (no play spam)
    const v = videoRef.current;
    if (v) v.playbackRate = playbackRate;
  }, [playbackRate]);
  //   type SortDir = "asc" | "desc";
  //   const [sortDir, setSortDir] = React.useState<SortDir>("asc");

  type SortKey = "reaction" | "rally";
  type SortDir = "asc" | "desc";

  const [sortKey, setSortKey] = React.useState<SortKey>("reaction");
  const [sortDir, setSortDir] = React.useState<SortDir>("asc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const [playerFilter, setPlayerFilter] = React.useState<number | null>(null);
  const [previewScale, setPreviewScale] = React.useState<1 | 2 | 4 | 8>(2);

  const filteredClips = React.useMemo(() => {
    if (playerFilter == null) return clips;
    return clips.filter((c) => c.event.playerId === playerFilter);
  }, [clips, playerFilter]);

  //const filteredClips = clips; // (we’ll insert player filtering below)
  const sortedClips = React.useMemo(() => {
    const arr = [...filteredClips];
    arr.sort((a, b) => {
      const av =
        sortKey === "reaction" ? a.event.reactionMs : a.event.rallyIndex;
      const bv =
        sortKey === "reaction" ? b.event.reactionMs : b.event.rallyIndex;
      const d = av - bv;
      return sortDir === "asc" ? d : -d;
    });
    return arr;
  }, [filteredClips, sortKey, sortDir]);

  const playerIds = React.useMemo(() => {
    const s = new Set<number>();
    for (const c of clips) s.add(c.event.playerId);
    return Array.from(s).sort((a, b) => a - b);
  }, [clips]);

  //   React.useEffect(() => {
  //     if (!open) return;
  //     const first = sortedClips[0]?.key ?? null;
  //     setSelectedKey(first);
  //   }, [open, playerFilter, sortDir]); // keep it simple

  React.useEffect(() => {
    if (!open) return;
    const first = sortedClips[0]?.key ?? null;
    setSelectedKey(first);
  }, [open, playerFilter, sortKey, sortDir]);

  const shouldMute = playbackRate < 1;

  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    v.muted = shouldMute;
    v.defaultMuted = shouldMute;

    // If it's playing, nudge it to keep going (Safari/Chrome can hiccup on mute toggles)
    if (!v.paused) v.play().catch(() => {});
  }, [shouldMute, videoUrl]);

  React.useEffect(() => {
    if (!open) return;
    const first = sortedClips[0]?.key ?? null;
    setSelectedKey(first);
  }, [open, playerFilter, sortKey, sortDir]);

  // --- dragging (unchanged) ---

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      const nextX = Math.max(
        0,
        Math.min(window.innerWidth - 220, d.startPosX + dx),
      );
      const nextY = Math.max(
        0,
        Math.min(window.innerHeight - 140, d.startPosY + dy),
      );
      setPos({ x: nextX, y: nextY });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  // --- dual video state like FastShots ---
  // const clipVideoRef = React.useRef<HTMLVideoElement | null>(null);
  // const rallyVideoRef = React.useRef<HTMLVideoElement | null>(null);

  // const [clipRate, setClipRate] = React.useState<0.25 | 0.5 | 1>(1);
  // const [rallyRate, setRallyRate] = React.useState<0.25 | 0.5 | 1>(1);

  // const [clipPaused, setClipPaused] = React.useState(false);
  // const [rallyPaused, setRallyPaused] = React.useState(false);

  // const clipShouldMute = clipRate < 1;
  // const rallyShouldMute = rallyRate < 1;

  // ActiveRallyVideo has its own internal video ref + controls.
  // We just provide a "seedPlayback" nonce so it can jump when selection changes.
  const [seedNonce, setSeedNonce] = React.useState(0);

  React.useEffect(() => {
    // bump nonce anytime the selected clip changes
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [
    selected?.event?.rallyIndex,
    selected?.event?.shotIndex,
    Math.round(selected?.clipStartMs ?? 0),
    Math.round(selected?.clipEndMs ?? 0),
  ]);

  const clipUrl = React.useMemo(() => {
    if (!selected) return null;
    return buildRallySegmentUrl({
      packageId,
      rallyIndex: selected.event.rallyIndex,
      startMs: Math.round(selected.clipStartMs),
      endMs: Math.round(selected.clipEndMs),
      download: 0,
    });
  }, [packageId, selected?.key]);

  // React.useEffect(() => {
  //   const v = clipVideoRef.current;
  //   if (!v) return;
  //   v.muted = clipShouldMute;
  //   v.defaultMuted = clipShouldMute;
  // }, [clipShouldMute, clipUrl]);

  // full rally url (optional)
  const selectedRow = React.useMemo(() => {
    if (!rows || !selected) return null;
    return rows[selected.event.rallyIndex] ?? null;
  }, [rows, selected?.key]);

  const clipRow = React.useMemo(() => {
    if (!selectedRow || !selected || !clipUrl) return null;

    return {
      ...selectedRow,

      // IMPORTANT: drive the top viewer with the TRIMMED clip url
      videoUrl: clipUrl,

      // IMPORTANT: these define the timeline base for overlays/looping/etc
      segmentStartMs: selected.clipStartMs,
      segmentEndMs: selected.clipEndMs,
      shotNumberOverlays: normalizeFastReactionClipOverlays({
        overlays: selectedRow.shotNumberOverlays,
        clipStartMs: selected.clipStartMs,
        clipEndMs: selected.clipEndMs,
        selectedShotNumber: selected.event.shotNumber,
      }),
    };
  }, [selectedRow, selected?.key, clipUrl]);

  const rallyVideoUrl = selectedRow?.videoUrl ?? null;

  const selectedShotClipTimeSec = React.useMemo(() => {
    if (!selected) return 0;
    return Math.max(
      0,
      (selected.event.shotStartMs - selected.clipStartMs) / 1000,
    );
  }, [selected?.key]);

  const selectedLoopRegion = React.useMemo(() => {
    if (!selected) return null;
    const clipDurationSec = Math.max(
      0.4,
      (selected.clipEndMs - selected.clipStartMs) / 1000,
    );
    const aSec = Math.max(
      0,
      (selected.event.shotStartMs - selected.clipStartMs) / 1000,
    );
    const bSec = Math.min(
      clipDurationSec,
      Math.max(
        aSec + 0.45,
        (selected.event.shotEndMs - selected.clipStartMs) / 1000 + 0.25,
      ),
    );
    return { aSec, bSec };
  }, [selected?.key]);

  // React.useEffect(() => {
  //   const v = rallyVideoRef.current;
  //   if (!v) return;
  //   v.muted = rallyShouldMute;
  //   v.defaultMuted = rallyShouldMute;
  // }, [rallyShouldMute, rallyVideoUrl]);

  // React.useEffect(() => setClipPaused(false), [clipUrl]);
  // React.useEffect(() => setRallyPaused(false), [rallyVideoUrl]);

  if (!open) return null;

  // NOTE: I'm not using MUI Popover here because you have your own floating panel patterns.
  // If you want this anchored like your other popups, swap outer container with Popover.

  return (
    <Paper
      elevation={10}
      data-happy-path="fastReactions-popup"
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: "calc(100vw - 36px)", //1320,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",
        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        border: "1px solid rgba(0,0,0,0.14)",

        // border: "3px solid yellow !important",
      }}
    >
      {/* Header (single row) */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        {/* left side: force ONE row; allow horizontal scroll if too many pills */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
            flexWrap: "nowrap",
            overflow: "hidden",
          }}
        >
          <Typography sx={{ fontWeight: 900, mr: 1, whiteSpace: "nowrap" }}>
            <Box
              sx={{
                display: "inline-block",
                transform: "translateY(3px)",
                marginRight: "3px",
              }}
            >
              {" "}
              {iconReaction(18, "rgba(0,0,0,1)")}
            </Box>
            Fast Reactions
          </Typography>

          <Typography
            sx={{ opacity: 0.75, fontSize: 12, whiteSpace: "nowrap" }}
          >
            {filteredClips.length} clips
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              ml: 1,
              flexWrap: "nowrap",
              overflowX: "auto",
              overflowY: "hidden",
              pb: 0.25,
              // keeps scrollbars subtle on mac; optional:
              // "&::-webkit-scrollbar": { height: 6 },
            }}
          >
            <Button
              size="small"
              variant="text" // {playerFilter == null ? "contained" : "outlined"}
              onClick={() => setPlayerFilter(null)}
              sx={{
                textTransform: "none",
                minHeight: 22,
                py: 0,
                px: 1,
                minWidth: 0,
                fontWeight: 900,
                fontSize: 12,
                border: "1px solid transparent",
                bgcolor: playerFilter == null ? "rgba(0,0,0,0.07)" : "white",
                boxShadow:
                  playerFilter == null
                    ? "inset 0 0 0 1px rgba(0,0,0,0.20)"
                    : "none",
                "&:hover": {
                  borderColor: "rgba(0,0,0,0.18)",
                  bgcolor:
                    playerFilter == null
                      ? "rgba(0,0,0,0.10)"
                      : "rgba(0,0,0,0.03)",
                },
              }}
              // sx={{
              //   textTransform: "none",
              //   minHeight: 22,
              //   py: 0,
              //   fontWeight: 900,
              //   fontSize: 12,
              //   whiteSpace: "nowrap",
              // }}
            >
              All
            </Button>

            {playerIds.map((pid) => {
              const isSelected = playerFilter === pid;
              const team = pid < 2 ? 0 : 1; // getTeamFromPlayerId(pid);

              return (
                <Button
                  key={pid}
                  size="small"
                  variant="text" // {playerFilter === pid ? "contained" : "outlined"}
                  onClick={() => setPlayerFilter(pid)}
                  sx={{
                    textTransform: "none",
                    minHeight: 22,
                    py: 0,
                    px: 0.5,
                    fontWeight: 900,
                    fontSize: 12,
                    whiteSpace: "nowrap",

                    border: "1px solid transparent",
                    bgcolor: isSelected
                      ? props.teamColor
                        ? props.teamColor(team, 0.18)
                        : "rgba(0,0,0,0.08)"
                      : "white",
                    boxShadow: isSelected
                      ? props.teamColor
                        ? `inset 0 0 0 1px ${props.teamColor(team, 0.55)}`
                        : "inset 0 0 0 1px rgba(0,0,0,0.22)"
                      : "none",

                    "&:hover": {
                      borderColor: props.teamColor
                        ? props.teamColor(team, 0.45)
                        : "rgba(0,0,0,0.18)",
                      bgcolor: isSelected
                        ? props.teamColor
                          ? props.teamColor(team, 0.24)
                          : "rgba(0,0,0,0.10)"
                        : "rgba(0,0,0,0.03)",
                    },
                  }}
                >
                  {renderPlayerIdentity
                    ? renderPlayerIdentity({
                        playerId: pid,
                        labelOnly: false,
                        size: 40,
                        borderRadius: 5,
                        fontSize: 12,
                      })
                    : playerLabel
                      ? playerLabel(pid)
                      : `p${pid}`}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Button
          size="small"
          variant="text"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}
        >
          Close
        </Button>
      </Box>

      <Box
        sx={{
          px: 1.25,
          py: 0.8,
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          bgcolor: "rgba(245,249,255,0.92)",
        }}
      >
        <Button
          size="small"
          variant={sortKey === "reaction" ? "contained" : "outlined"}
          onClick={() => toggleSort("reaction")}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Sort by reaction {sortKey === "reaction" ? (sortDir === "asc" ? "▲" : "▼") : ""}
        </Button>
        <Button
          size="small"
          variant={sortKey === "rally" ? "contained" : "outlined"}
          onClick={() => toggleSort("rally")}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Sort by rally {sortKey === "rally" ? (sortDir === "asc" ? "▲" : "▼") : ""}
        </Button>
        <FastReactionScaleControls
          value={previewScale}
          onChange={setPreviewScale}
        />
        <Typography sx={{ fontSize: 12, color: "rgba(0,0,0,0.62)" }}>
          Each card is a clipped review moment. Play it here or pop it out for
          normal active-rally controls.
        </Typography>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowX: "auto",
          overflowY: "hidden",
          p: 1.25,
          display: "grid",
          gridAutoFlow: "column",
          gridTemplateRows: `repeat(${previewScale}, minmax(0, 1fr))`,
          gridAutoColumns: `${fastReactionCardWidth(previewScale)}px`,
          alignItems: "stretch",
          gap: 1.25,
          bgcolor: "rgba(8,12,16,0.96)",
        }}
      >
        {sortedClips.length ? (
          sortedClips.map((clip, index) => (
            <FastReactionPreviewCard
              key={clip.key}
              clip={clip}
              index={index}
              cardWidth={fastReactionCardWidth(previewScale)}
              packageId={packageId}
              rows={rows}
              insightsToCvPlayerIndex={insightsToCvPlayerIndex}
              renderPlayerIdentity={renderPlayerIdentity}
              teamColor={teamColor}
              teamName={teamName}
              firefightsClips={firefightsClips}
              firefightRallySet={firefightRallySet}
            />
          ))
        ) : (
          <Box
            sx={{
              p: 2,
              border: "1px dashed rgba(255,255,255,0.18)",
              borderRadius: 1,
              color: "rgba(255,255,255,0.62)",
              fontSize: 12,
              gridColumn: "1 / -1",
            }}
          >
            No fast reactions for this selection.
          </Box>
        )}
      </Box>
    </Paper>
  );
}
